import type {
  VerifyTenantFeatures,
  VerifyTenantResponse,
  VerifyTenantUsers,
} from '../contracts/tenant-verification';

export interface IBrandDetails {
  domain: string
  customDomain: string
  apiUrl: string
  active: boolean
  users: IFeatureUsers
  features: IFeatures
  maintenance_mode: IMaintenance
  subscription_active: boolean,
  subscription_active_message: string,
  // SAN-384/386 — added by the cockpit's `resolve-domain/:hostname` (the strict
  // superset that replaced `verify_tenant/:hostname` as this app's bootstrap
  // call). Present ONLY when the visited hostname resolved through the
  // spoke-subdomain fallback (`aaf.thub.example.com` -> tenant `thub.example.com`
  // + `partnerAbbreviation: 'aaf'`). Absent — and therefore `undefined` — for
  // every hub/root hostname, exactly as before this feature.
  partnerAbbreviation?: string,
}

export interface IFeatureUsers {
  startups: boolean
  investors: boolean
  mentors: boolean
  corporates: boolean
  service_providers: boolean
  government: boolean
  partners: boolean
  program_offices: boolean
  program_offices_registration: boolean
  individuals: boolean
  individual_registration?:boolean

  // "individual_registration": false,
  service_providers_registration?: boolean,
  startups_registration?: boolean,
  investors_registration?: boolean,
  corporates_registration?: boolean,
}

export interface IFeatures {
  program_management_with_profile_form?: boolean
  facility_enable_checkin_checkout?: boolean
  individuals_title:string
  certificates: boolean
  startup_id_cards?: boolean
  // SAN-253 — Startup ID Creation Module (Digital ID Card). Propagated to
  // tenants/backend Feature enum/admin config.php already; this interface
  // was the one consumer missed until now (invariant #1, flag names owned
  // by tenants must reach all four consumers).
  startup_recognition_enabled?: boolean
  // When true, an investor can view a startup's pitch deck without needing
  // an active ACCEPTED connection to that startup first -- mirrors the
  // backend's Feature.INVESTOR_PITCH_DECK_ACCESS_ENABLED gate in
  // StartupService.canViewFullStartupInformation().
  investor_pitch_deck_access_enabled?: boolean
  // SAN-384/387 — Hub/Spoke domain & white-label architecture master gate.
  // Already sent by the cockpit's verify_tenant/resolve-domain response
  // (global.service.ts); this interface was the one consumer missed.
  hub_spoke_domain_enabled?: boolean
  // SAN-756/757/758 — Operational Cost Reimbursement (T-RISE). Gates the
  // eligibility grid + Month-1 claim form for recognized startups. Off by
  // default for every tenant except Tripura once enabled.
  operational_cost_reimbursement_enabled?: boolean
  // SAN-1677 / FA-011 — gates the "Limit Access" config UI in sc-saas-admin.
  // Typing only: jurors never use this app, so nothing here reads it.
  limit_jury_access_enabled?: boolean
  registration_invite_only: boolean
  registration_turnoff_programs: boolean
  online_meetings: boolean
  calendar: boolean
  profile_comparison: boolean
  chat: boolean
  startup_kit: boolean
  jobs: boolean
  business_challenges: boolean
  custom_forms: boolean
  mentor_hours: boolean
  community_feed: boolean
  startup_supporting_documents: boolean
  syndicate: boolean
  financials_form: boolean
  startup_onboarding_modal: boolean
  looking_for_section: boolean
  glossary: boolean
  resources_downloads: boolean
  connections: boolean
  registration_enterprise_sales_popup: boolean
  individual_investor: boolean
  post_revenue_fields: boolean
  email_otp_verification: boolean
  mobile_otp_verification: boolean
  whatsapp_otp_verification: boolean
  show_dashboard: boolean
  show_ecosystem: boolean
  video_pitch_mandatory: boolean
  profiles_locked: boolean
  new_search_layout: boolean
  main_website_url: string
  multiple_profiles: boolean
  logout_on_rejection: boolean;
  milestone_management: boolean;
  ticket_management: boolean;
  video_types: 'both' | 'upload_video' | 'power_pitch'
  menu_type: MenuTypes
  sidebar_color: SideBarColors
  direct_accept_connection_enabled: boolean;
  filing_email_address_enabled: boolean;
  can_deactivate_profile: boolean;
  can_delete_profile: boolean;
  reports_downloads: boolean;
  deeptech_news: boolean;
  webinar_videos: boolean;
  mentor_hours_title: string
  mentors_title:string
  community_feed_title: string
  milestone_management_title: string
  growth_metrics_title: string
  business_challenges_title: string
  events_title: string
  call_for_applications_menu_title:string
  startup_kit_title: string
  resources_downloads_title: string
  glossary_title: string

  ticket_management_title: string
  reports_downloads_title: string
  growth_metrics_duration: string,
  // "quarterly"
  deeptech_news_title: string
  webinar_videos_title: string
  growth_metrics: boolean;
  events: boolean;
  program_offices_title: string
  primary_industry: boolean;
  elastic_search: boolean,
  search_type: "elastic" | 'normal',
  currency: string;
  chat_type: 'inhouse' | 'comet_chat';

  startup_to_mentor_connection_questions: string;
  ask_startup_to_mentor_connection_question: boolean

  partners_title: string;
  startups_title: string;
  service_providers_title: string;
  disable_startup_profile_update: boolean
  startup_show_programs: boolean

  intellectual_property_section: boolean;
  investment_banking_section: boolean;
  company_identification_cin: boolean;
  company_identification_gst: boolean;
  company_identification_dpiit: boolean;
  limited_access: boolean;
  enable_sub_industries: boolean
  meeting_time_slot_difference_in_mins: number

  external_sign_in_enabled: boolean;
  external_sign_in_name: string;
  programs_public_view?: boolean
  vs_programs_public_view?: boolean
  industries_technologies_section: boolean
  mentorship_areas_new_layout: boolean
  growth_metrics_financial_year_start_month:number
  // individual_registration: boolean
  ip_management: boolean;

  corporates_enagement_form: boolean;
  new_dashboard_layout?: boolean;

  facility_management: boolean;
  new_dashboard_header_color?: string
  new_dashboard_community_post_view?: 'inline' | 'default'
  startup_founders_advisory:boolean;
  startup_district_subdistrict_enabled:boolean;
  startup_city_enabled:boolean;
  mentor_domain_expertise_tab: boolean;

  application_management_check_status_section?: boolean;
  application_management_round_status_updates?: boolean;
  programs_payment_enabled?:boolean;
  call_for_applications_payment_enabled?:boolean;
  call_for_applications_document_enabled?:boolean;
  incubator_module_enabled? :boolean
  membership_moderation_enabled:boolean;
  membership_enabled:boolean;
  membership_label_text:string;
  payment_currency:string;
  membership_visibility_type: 'anyone' | 'approved' | 'manual';
  challenges_collections_filters_enabled:boolean;
  corporate_profile_registration_confirmation_enabled:boolean;
  corporate_backdoor_login_enabled:boolean;
  business_challenge_collections_enabled: boolean,
  meeting_moderation_enabled:boolean;
   learning_management:boolean;
   facility_module_type: 'internal' | 'external';
  events_agenda_menu_enabled:boolean;
  programs_menu_enabled:boolean;
  events_exhibitors_menu_enabled:boolean;
  reset_application_title:string;
  // SAN-246/247/248: platform-operator-gated (sanchiconnect-saas-tenants-admin
  // only, not tenant self-serve) per-tenant custom branding for all 10
  // onboarding screens. Gates reading `IGlobalSettings.onboardingDesign`.
  custom_onboarding_design_enabled?: boolean;
  // Set to true by the tenant admin's "Go Live" action in sc-saas-admin.
  // Both this and custom_onboarding_design_enabled must be true for
  // custom onboarding to appear for users.
  custom_onboarding_design_golive_enabled?: boolean;
  // SAN-337 — global, opt-in switch (default false). When on, a startup
  // whose profile is unapproved gets the Apply button/link on both the
  // Program-rounds and CFA surfaces swapped for an inline disabled/
  // pending state instead of being able to apply (real enforcement is
  // still the backend's ForbiddenException on both apply routes).
  approved_startups_can_apply_on_programs?: boolean;
  // SAN-1428 — Notifications BRD v1.1 (NOTIF-001) master gate, default
  // false. Off: no bell, no new badges, /notifications behaves as before.
  // Strictly `=== true` — a missing key means off (backend
  // NotificationCentreGuard uses the same rule).
  notification_centre_enabled?: boolean;
}

export enum MenuTypes {
  TOP = 'top',
  SIDE = 'side',
}

export enum SideBarColors {
  LIGHT = 'light',
  DARK = 'dark',
}
export interface IMaintenance {
  id: number
  title: string
  message: string
  date: string // convert in utc-local
  startTime: string // convert in utc-local
  endTime: string // convert in utc-local
  active: boolean
  maintenance_on: boolean
  actual_start_time: any
  actual_end_time: any
  notes: string

  maintenance_on_message:string
  maintenance_on_title:string
}

/* SAN-1595 — compile-time contract check against the cockpit's verify_tenant / resolve-domain
   shape (core/contracts/tenant-verification.ts, a generated copy kept identical to
   sanchiconnect-saas-tenants/contracts/generated/tenant-verification.ts). If one of these lines
   fails to compile, the PWA declares a key the cockpit does not send: the error names the key.
   Fix the interface, or add the field in the cockpit and re-sync the generated copy. */
type NotDelivered<T> = [T] extends [never]
  ? true
  : { notDeliveredByTheCockpit: T };
export const brandDetailsKeysDelivered: NotDelivered<
  Exclude<keyof IBrandDetails, keyof VerifyTenantResponse>
> = true;
export const featureUsersKeysDelivered: NotDelivered<
  Exclude<keyof IFeatureUsers, keyof VerifyTenantUsers>
> = true;
export const featuresKeysDelivered: NotDelivered<
  Exclude<keyof IFeatures, keyof VerifyTenantFeatures>
> = true;
